/* ==========================================================================
   NGA — Motion & interaction engine
   Zero dependencies. ~4kb gzipped. Everything degrades to plain HTML.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supportsIO = 'IntersectionObserver' in window;

  var NGA = (window.NGA = window.NGA || {});

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 120);
    };
  }

  function throttleRAF(fn) {
    var ticking = false;
    return function () {
      var ctx = this, args = arguments;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { fn.apply(ctx, args); ticking = false; });
    };
  }

  NGA.$ = $; NGA.$$ = $$; NGA.debounce = debounce;

  /* ---------------------------------------------------------------------
     Slow zoom on campaign imagery

     Separate from the reveal system: the reveal pass moves opacity and
     transform on the element itself, which would fight the scale on the
     image inside it.
     --------------------------------------------------------------------- */
  var zoomObserver = null;

  function initZoom(root) {
    var items = $$('.nga-zoom-slow:not([data-nga-zoom-done])', root);
    if (!items.length) return;

    items.forEach(function (el) { el.setAttribute('data-nga-zoom-done', ''); });

    if (reduce.matches || !supportsIO) return;

    if (!zoomObserver) {
      zoomObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          zoomObserver.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
    }

    items.forEach(function (el) { zoomObserver.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Scroll reveal (with stagger)
     --------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveal(root) {
    var items = $$('.nga-reveal:not([data-nga-revealed])', root);
    if (!items.length) return;

    if (reduce.matches || !supportsIO) {
      items.forEach(function (el) {
        el.classList.add('is-in');
        el.setAttribute('data-nga-revealed', '');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.classList.add('is-in');
          revealObserver.unobserve(el);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }

    items.forEach(function (el) {
      el.setAttribute('data-nga-revealed', '');
      // Stagger siblings inside a group without authoring per-item delays
      var group = el.closest('[data-nga-stagger]');
      if (group && !el.style.getPropertyValue('--nga-delay')) {
        var step = parseInt(group.getAttribute('data-nga-stagger'), 10) || 70;
        var sibs = $$('.nga-reveal', group);
        var i = sibs.indexOf(el);
        if (i > 0) el.style.setProperty('--nga-delay', Math.min(i * step, 600) + 'ms');
      }
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Split headline into animated words
     --------------------------------------------------------------------- */
  function initSplit(root) {
    $$('[data-nga-split]:not([data-nga-split-done])', root).forEach(function (el) {
      el.setAttribute('data-nga-split-done', '');
      if (reduce.matches) return;

      var step = parseInt(el.getAttribute('data-nga-split'), 10) || 55;
      var words = el.textContent.trim().split(/\s+/);
      var frag = document.createDocumentFragment();

      words.forEach(function (w, i) {
        var outer = document.createElement('span');
        outer.className = 'nga-split-word';
        var inner = document.createElement('span');
        inner.textContent = w;
        inner.style.setProperty('--nga-delay', i * step + 'ms');
        outer.appendChild(inner);
        frag.appendChild(outer);
        if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
      });

      el.textContent = '';
      el.appendChild(frag);
    });
  }

  /* ---------------------------------------------------------------------
     Image fade-in once decoded
     --------------------------------------------------------------------- */
  function initImageFade(root) {
    $$('img.nga-fade:not([data-nga-fade-done])', root).forEach(function (img) {
      img.setAttribute('data-nga-fade-done', '');
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('is-loaded'); }, { once: true });
      }
    });
  }

  /* ---------------------------------------------------------------------
     Marquee — duplicate track so the loop is seamless
     --------------------------------------------------------------------- */
  function initMarquee(root) {
    $$('.nga-marquee:not([data-nga-marquee-done])', root).forEach(function (el) {
      el.setAttribute('data-nga-marquee-done', '');
      var track = $('.nga-marquee__track', el);
      if (!track) return;
      var item = $('.nga-marquee__item', track);
      if (!item) return;
      // One duplicate is all a -50% translate needs.
      if (track.children.length < 2) track.appendChild(item.cloneNode(true));
      track.setAttribute('aria-hidden', 'false');
      var clone = track.children[1];
      if (clone) clone.setAttribute('aria-hidden', 'true');
    });
  }

  /* ---------------------------------------------------------------------
     Lazy video — only play what is on screen, and never on save-data
     --------------------------------------------------------------------- */
  function initLazyVideo(root) {
    var vids = $$('video[data-nga-autoplay]:not([data-nga-video-done])', root);
    if (!vids.length) return;

    var conn = navigator.connection || {};
    var saveData = conn.saveData === true;
    var slow = /2g/.test(conn.effectiveType || '');

    vids.forEach(function (v) { v.setAttribute('data-nga-video-done', ''); });

    if (saveData || slow || reduce.matches || !supportsIO) {
      // Leave the poster in place and expose native controls instead.
      vids.forEach(function (v) { v.setAttribute('controls', ''); v.removeAttribute('data-nga-autoplay'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          // Attach the real source only when it is first needed.
          if (!v.getAttribute('data-nga-src-applied')) {
            $$('source[data-src]', v).forEach(function (s) { s.src = s.getAttribute('data-src'); });
            v.setAttribute('data-nga-src-applied', '1');
            v.load();
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () { /* autoplay blocked — poster stays */ });
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.35 });

    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------------------------------------------------------------------
     Count-up numbers
     --------------------------------------------------------------------- */
  function initCount(root) {
    var els = $$('[data-nga-count]:not([data-nga-count-done])', root);
    if (!els.length) return;

    els.forEach(function (el) { el.setAttribute('data-nga-count-done', ''); });

    if (reduce.matches || !supportsIO) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-nga-count'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var target = parseFloat(el.getAttribute('data-nga-count')) || 0;
        var suffix = el.getAttribute('data-nga-count-suffix') || '';
        var dur = 1200, start = null;
        function tick(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased).toLocaleString() + suffix;
          if (p < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Sticky header state
     --------------------------------------------------------------------- */
  function initHeaderState() {
    var header = $('[data-nga-header]');
    if (!header) return;
    var last = 0;
    var onScroll = throttleRAF(function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      header.classList.toggle('is-stuck', y > 8);
      // Default is a header that always stays put. Opt in to hide-on-scroll
      // per theme setting when a taller header needs to get out of the way.
      if (header.getAttribute('data-sticky') === 'hide-on-scroll') {
        if (y > 320 && y > last + 4) header.classList.add('is-hidden');
        else if (y < last - 4 || y < 320) header.classList.remove('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      last = y;
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------------
     Rail controls (prev/next + progress) for horizontal carousels
     --------------------------------------------------------------------- */
  function initRails(root) {
    $$('[data-nga-rail]:not([data-nga-rail-done])', root).forEach(function (wrap) {
      wrap.setAttribute('data-nga-rail-done', '');
      var rail = $('.nga-rail', wrap) || $('[data-nga-rail-track]', wrap);
      if (!rail) return;
      var prev = $('[data-nga-rail-prev]', wrap);
      var next = $('[data-nga-rail-next]', wrap);
      var bar = $('[data-nga-rail-progress]', wrap);

      function step() {
        var first = rail.firstElementChild;
        if (!first) return rail.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 16;
        return first.getBoundingClientRect().width + gap;
      }

      function update() {
        var max = rail.scrollWidth - rail.clientWidth;
        var pct = max > 2 ? rail.scrollLeft / max : 0;
        if (prev) prev.disabled = rail.scrollLeft < 4;
        if (next) next.disabled = rail.scrollLeft > max - 4;
        if (bar) bar.style.transform = 'scaleX(' + Math.max(pct, 0.06) + ')';
        wrap.classList.toggle('is-scrollable', max > 4);
      }

      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: reduce.matches ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: reduce.matches ? 'auto' : 'smooth' }); });

      rail.addEventListener('scroll', throttleRAF(update), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------------------------------------------------------------------
     Accordions (FAQ, product details, filters)
     --------------------------------------------------------------------- */
  function initAccordion(root) {
    $$('[data-nga-accordion]:not([data-nga-acc-done])', root).forEach(function (group) {
      group.setAttribute('data-nga-acc-done', '');
      var single = group.getAttribute('data-nga-accordion') === 'single';
      if (!single) return;
      group.addEventListener('toggle', function (e) {
        var d = e.target;
        if (d.tagName !== 'DETAILS' || !d.open) return;
        $$('details[open]', group).forEach(function (other) {
          if (other !== d && other.parentElement === d.parentElement) other.open = false;
        });
      }, true);
    });
  }

  /* ---------------------------------------------------------------------
     Toasts
     --------------------------------------------------------------------- */
  NGA.toast = function (message, opts) {
    opts = opts || {};
    var host = $('.nga-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'nga-toast-host';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }
    var el = document.createElement('div');
    el.className = 'nga-toast' + (opts.variant ? ' nga-toast--' + opts.variant : '');
    el.innerHTML = message;
    host.appendChild(el);
    setTimeout(function () {
      el.style.transition = 'opacity .3s, transform .3s';
      el.style.opacity = '0';
      el.style.transform = 'translateY(8px)';
      setTimeout(function () { el.remove(); }, 320);
    }, opts.duration || 3200);
    return el;
  };

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot(root) {
    initSplit(root);
    initReveal(root);
    initZoom(root);
    initImageFade(root);
    initMarquee(root);
    initLazyVideo(root);
    initCount(root);
    initRails(root);
    initAccordion(root);
  }

  NGA.boot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); initHeaderState(); }, { once: true });
  } else {
    boot(document);
    initHeaderState();
  }

  // Theme editor: re-run on section render so previews stay live.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); initHeaderState(); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });

  // Re-scan after AJAX (facets, load-more, quick view).
  document.addEventListener('nga:refresh', function (e) { boot((e.detail && e.detail.root) || document); });
})();

/* ==========================================================================
   NGA — mobile accordions

   Elements marked [data-nga-mobile-accordion] render <details open> so the
   content is never hidden if scripting fails. On small screens they are
   closed here, turning them into real accordions.
   ========================================================================== */
(function () {
  'use strict';
  var mq = window.matchMedia('(max-width: 749px)');

  function sync() {
    document.querySelectorAll('[data-nga-mobile-accordion]').forEach(function (el) {
      if (mq.matches) {
        // Only auto-close once, so a shopper's own toggle is respected.
        if (!el.hasAttribute('data-nga-collapsed')) {
          el.open = false;
          el.setAttribute('data-nga-collapsed', '');
        }
      } else {
        el.open = true;
        el.removeAttribute('data-nga-collapsed');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
  if (mq.addEventListener) mq.addEventListener('change', sync);
  else if (mq.addListener) mq.addListener(sync);
  document.addEventListener('shopify:section:load', sync);
})();
